// All subscription tiers information (Unified Architecture)
// No more separation between personal and organization tiers

export type Tier = 'free' | 'standard' | 'pro' | 'team' | 'club' | 'pro_team';

/**
 * Лимиты тарифа числами. Должны совпадать с GetOrganizationLimits в
 * internal/domain/organization/limits.go - это проверяет tiers.sync.test.ts.
 * -1 - без ограничения.
 */
export interface TierLimits {
  members: number;
  directions: number;
  athletes: number;
  groups: number;
  coaches: number;
  aiRequests: number;
}

/**
 * Запросов Штаба в месяц в пробном периоде любого тарифа
 * (TrialAIRequestsLimit в limits.go): пробный период - не полный лимит тарифа.
 */
export const TRIAL_AI_REQUESTS = 50;

/**
 * Пробный период - один раз на клуб (organizations.trial_used_at): после него
 * платный тариф только с оплатой. Тот же текст во всех экранах.
 */
export const TRIAL_ONCE_RULE = 'Пробный период платного тарифа - один раз на клуб.';

/** Строка возможностей тарифа про пробный период. */
function trialFeature(days: number): string {
  return `${days} дней пробного периода, один раз на клуб`;
}

export interface TierInfo {
  id: string;
  name: string;
  limits: TierLimits;
  price: number; // Monthly price in rubles
  priceYearly?: number; // Yearly price in rubles (17% discount)
  topFeatures: string[]; // Top 3-4 features for compact card display
  features: string[]; // Full list of features
  maxMembers: number; // 1 = solo, >1 = team
  trialDays: number; // Trial period duration (0 = no trial)
  highlight?: boolean;
}

// All tiers (no separation)
// Trial periods: STANDARD/TEAM → 7 days, PRO/CLUB/PRO_TEAM → 14 days
export const ALL_TIERS: Record<Tier, TierInfo> = {
  free: {
    id: 'free',
    limits: { members: 1, directions: 1, athletes: 40, groups: 1, coaches: 2, aiRequests: 30 },
    name: 'FREE',
    price: 0,
    maxMembers: 1,
    trialDays: 0,
    topFeatures: [
      '1 направление',
      '40 спортсменов',
      '1 группа',
      '30 AI-запросов'
    ],
    features: [
      '1 участник',
      '1 направление',
      'До 40 спортсменов',
      '1 группа',
      'До 2 тренеров',
      '30 AI-запросов/мес'
    ]
  },
  standard: {
    id: 'standard',
    limits: { members: 1, directions: 3, athletes: 50, groups: 5, coaches: 10, aiRequests: 150 },
    name: 'STANDARD',
    price: 490,
    priceYearly: 4900,
    maxMembers: 1,
    trialDays: 7,
    topFeatures: [
      '3 направления',
      '50 спортсменов',
      '5 групп',
      '150 AI-запросов'
    ],
    features: [
      '1 участник',
      'До 3 направлений',
      'До 50 спортсменов',
      'До 5 групп',
      'До 10 тренеров',
      '150 AI-запросов/мес',
      trialFeature(7)
    ]
  },
  pro: {
    id: 'pro',
    limits: { members: 1, directions: 10, athletes: 200, groups: 20, coaches: 30, aiRequests: 500 },
    name: 'PRO',
    price: 990,
    priceYearly: 9900,
    maxMembers: 1,
    trialDays: 14,
    topFeatures: [
      '10 направлений',
      '200 спортсменов',
      '20 групп',
      '500 AI-запросов'
    ],
    features: [
      '1 участник',
      'До 10 направлений',
      'До 200 спортсменов',
      'До 20 групп',
      'До 30 тренеров',
      '500 AI-запросов/мес',
      trialFeature(14)
    ],
    highlight: true
  },
  team: {
    id: 'team',
    limits: { members: 3, directions: 10, athletes: 100, groups: 20, coaches: 10, aiRequests: 500 },
    name: 'TEAM',
    price: 1990,
    priceYearly: 19900,
    maxMembers: 3,
    trialDays: 7,
    topFeatures: [
      '3 участника',
      '100 спортсменов',
      '20 групп',
      '500 AI-запросов'
    ],
    features: [
      'До 3 участников',
      'До 10 направлений',
      'До 100 спортсменов',
      'До 20 групп',
      'До 10 тренеров',
      '500 AI-запросов/мес',
      trialFeature(7)
    ]
  },
  club: {
    id: 'club',
    limits: { members: 10, directions: 30, athletes: 500, groups: 50, coaches: 30, aiRequests: 1000 },
    name: 'CLUB',
    price: 4990,
    priceYearly: 49900,
    maxMembers: 10,
    trialDays: 14,
    topFeatures: [
      '10 участников',
      '500 спортсменов',
      '50 групп',
      '1000 AI-запросов'
    ],
    features: [
      'До 10 участников',
      'До 30 направлений',
      'До 500 спортсменов',
      'До 50 групп',
      'До 30 тренеров',
      '1 000 AI-запросов/мес',
      trialFeature(14)
    ],
    highlight: true
  },
  pro_team: {
    id: 'pro_team',
    limits: { members: 30, directions: 100, athletes: 2000, groups: 200, coaches: 100, aiRequests: 5000 },
    name: 'PRO TEAM',
    price: 9990,
    priceYearly: 99900,
    maxMembers: 30,
    trialDays: 14,
    topFeatures: [
      '30 участников',
      '2000 спортсменов',
      '200 групп',
      '5000 AI-запросов'
    ],
    features: [
      'До 30 участников',
      'До 100 направлений',
      'До 2 000 спортсменов',
      'До 200 групп',
      'До 100 тренеров',
      '5 000 AI-запросов/мес',
      trialFeature(14)
    ]
  }
};

// Helper to get all tiers as array
export function getAllTiers(): TierInfo[] {
  return Object.values(ALL_TIERS);
}

// Helper to get tier by id
export function getTierById(id: string): TierInfo | undefined {
  return ALL_TIERS[id as Tier];
}

// Helper to format price
export function formatPrice(price: number): string {
  if (price === 0) return 'Бесплатно';
  return `${price.toLocaleString('ru-RU')} ₽/мес`;
}

// Helper to format yearly price
export function formatYearlyPrice(price: number): string {
  if (price === 0) return '';
  return `${price.toLocaleString('ru-RU')} ₽/год`;
}

// Helper to check if tier is for teams
export function isTeamTier(tier: TierInfo): boolean {
  return tier.maxMembers > 1 || tier.maxMembers === -1;
}

// Helper to format trial period
export function formatTrialPeriod(days: number): string {
  if (days === 0) return '';
  return trialFeature(days);
}
